import {
  Form,
  Input,
  message,
  InputNumber,
  DatePicker,
  Checkbox,
  Radio,
} from "antd";
import Button from "../common/button";
import Dropdown from "../common/dropdown";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { fetchBusiness } from "../../store/slices/businessSlice";
import { getBusinessServices } from "../../api/service";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { createPromoCode, updatePromoCode } from "../../api/user";
import { getCustomersList } from "../../api/customer";
import { getServiceCategories } from "../../api/service";
import colors from "../../share/constants/constants";
import SubMenuDropdown from "../common/subMenuDropdown";

const PromoCodeForm = ({ linkBack, editPromoCode, getPromoCodes }) => {
  const businessNames = useSelector((state) => state?.business?.res?.items);
  const isBusinessLoading = useSelector((state) => state?.business?.isLoading);
  const dispatch = useDispatch();
  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [selectBusinessId, setSelectBusinessId] = useState(null);
  const [selectedKey, setSelectedKey] = useState(
    editPromoCode?.promoType === "GLOBAL"
      ? "global"
      : editPromoCode?.serviceDetails?.length
      ? "service"
      : editPromoCode?.categoryId?.length
      ? "category"
      : "user"
  );
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loader, setLoader] = useState(false);
  const [startDate, setStartDate] = useState(
    editPromoCode?.startDate ? dayjs(editPromoCode?.startDate) : null
  );
  const [allUsersAllServices, setAllUsersAllServices] = useState(
    editPromoCode?.promoType === "GLOBAL" ? true : false
  );

  const [form] = Form.useForm();
  dayjs.extend(customParseFormat);
  const { RangePicker } = DatePicker;

  const range = (start, end) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  const disabledStartDate = (current) => {
    return current && current < dayjs().startOf("day");
  };

  const disabledEndDate = (current) => {
    return (
      current && (current < dayjs().startOf("day") || current <= startDate)
    );
  };

  // useEffect(() => {
  //   if(editPromoCode) {
  //     if(editPromoCode?.promoType) {
  //       setSelectedKey(editPromoCode?.promoType)
  //     }
  //   }
  // }, [editPromoCode])

  const onFinish = async (values) => {
    const payload = {
      promoType: allUsersAllServices
        ? "GLOBAL"
        : values?.userId?.length
        ? "USER"
        : values?.categoryId
        ? "CATEGORY"
        : values?.variantId
        ? "VARIANT"
        : "SERVICE",
      promoCode: values?.promoCode,
      discountAmount: values?.discountAmount,
      isPercent: false,
      startDate: values?.startDate,
      expiryDate: values?.expiryDate,
    };

    if (values?.userId?.length) {
      payload.userIds = values?.userId;
    }
    if (values?.categoryId) {
      payload.categoryId = values?.categoryId;
    }
    if (values?.serviceId || values?.variantId) {
      payload.serviceId = values?.serviceId ?? values?.variantId;
    }
    if (editPromoCode) {
      setLoader(true);
      const res = await updatePromoCode(editPromoCode?._id, payload);
      if (res) {
        form.resetFields();
        setSelectBusinessId(null);
        setServices([]);
        setStartDate(null);
        message.success("Promo Code has been updated.");
        getPromoCodes();
        linkBack();
      }
      setLoader(false);
    } else {
      setLoader(true);
      const res = await createPromoCode(payload);
      if (res) {
        form.resetFields();
        setSelectBusinessId(null);
        setServices([]);
        setStartDate(null);
        message.success("Promo Code has been created.");
        getPromoCodes();
        linkBack();
      }
      setLoader(false);
    }
  };

  const loadMoreData = (page) => {
    const payload = {
      pageNumber: page,
      itemPerPage: 50,
    };
    dispatch(fetchBusiness(payload));
  };

  const loadMoreUserData = (page) => {
    getCustomers(page, "");
  };

  useEffect(() => {
    const payload = {
      pageNumber: 1,
      itemPerPage: editPromoCode?.serviceDetails?.length ? 1000 : 50,
    };
    dispatch(fetchBusiness(payload));
  }, []);

  useEffect(() => {
    const currentPage = 1;
    getCustomers(currentPage, "");
    getCategories();
  }, []);

  const getCustomers = async (currentPage, text) => {
    const pageSize = editPromoCode?.assignedUsers?.length ? 5000 : 50;
    setIsUserLoading(true);
    const res = await getCustomersList(currentPage, pageSize, text);
    if (res && res?.items?.length) {
      setUsers((prevUsers) => [...prevUsers, ...res?.items]);
    }
    setIsUserLoading(false);
  };

  const getServicesByBusinessId = async (businessId, page) => {
    setIsServiceLoading(true);
    const res = await getBusinessServices({
      pageNumber: page,
      pageSize: editPromoCode?.serviceDetails?.length ? 1000 : 50,
      businessId: businessId,
    });
    if (res) {
      setServices((prevServices) => [...prevServices, ...res?.items]);
    }
    setIsServiceLoading(false);
  };

  const handleBusinessChange = async (businessId) => {
    setSelectBusinessId(businessId);
    setServices([]);
    form.setFieldsValue({ serviceId: undefined });
    await getServicesByBusinessId(businessId, 1);
  };

  const loadMoreServiceData = async (page) => {
    await getServicesByBusinessId(selectBusinessId, page);
  };

  const navItems = [
    { key: "user", title: "User", link: "/analytics" },
    { key: "service", title: "Service", link: "/services" },
    { key: "category", title: "Category", link: "/services" },
    { key: "global", title: "Global", link: "/global" },
  ];

  const getCategories = async () => {
    setIsCategoryLoading(true);
    try {
      const res = await getServiceCategories();
      if (res) {
        setCategories(res);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsCategoryLoading(false);
    }
  };
  // Set initial values for the form
  useEffect(() => {
    if (editPromoCode) {
      const {
        promoCode,
        discountAmount,
        expiryDate,
        assignedUsers,
        serviceDetails,
        startDate,
        categoryId,
      } = editPromoCode;
      // Set initial form values
      form.setFieldsValue({
        promoCode,
        discountAmount,
        startDate: startDate ? dayjs(startDate) : "",
        expiryDate: expiryDate ? dayjs(expiryDate) : "",
      });
      setStartDate(startDate ? dayjs(startDate) : null);
      // Handle setting userId
      if (assignedUsers) {
        form.setFieldsValue({
          userId: assignedUsers?.map((user) => user.user._id),
        });
      }
      // Handle setting categoryId
      if (categoryId && categoryId.length > 0) {
        form.setFieldsValue({
          categoryId: categoryId[0]._id,
        });
      }
      // Handle setting wenderId and serviceId
      if (serviceDetails && serviceDetails?.length > 0) {
        form.setFieldsValue({
          wenderId: serviceDetails[0].businessId?._id,
        });
        setSelectBusinessId(serviceDetails[0].businessId?._id);
        // Fetch services for the selected business
        getServicesByBusinessId(serviceDetails[0].businessId?._id, 1);
      }
      if (serviceDetails && serviceDetails?.length > 0) {
        if (serviceDetails[0]?.parentVariant) {
          form.setFieldsValue({
            variantId: serviceDetails[0]._id,
          });
        } else {
          form.setFieldsValue({
            serviceId: serviceDetails[0]._id,
          });
        }
      }
    }
  }, [editPromoCode]);
  useEffect(() => {
    if (!editPromoCode) {
      form.resetFields([
        "serviceId",
        "wenderId",
        "userId",
        "categoryId",
        "variantId",
      ]);
      setAllUsersAllServices(false);
    }
  }, [selectedKey]);
  useEffect(() => {
    if (editPromoCode?.promoType === "VARIANT") {
      setSelectedKey("variant");
    } else if (editPromoCode?.promoType === "SERVICE") {
      setSelectedKey("service");
    } else if (editPromoCode?.promoType === "GLOBAL") {
      setSelectedKey("global");
    } else if (editPromoCode?.promoType === "USER") {
      setSelectedKey("user");
    } else if (editPromoCode?.promoType === "CATEGORY") {
      setSelectedKey("category");
    }
  }, [editPromoCode]);
  
  return (
    <div>
      <Form onFinish={onFinish} form={form} layout="vertical">
        <div className="sm:grid sm:grid-cols-2 gap-x-2 gap-y-1 px-2">
          <Form.Item
            name="promoCode"
            label="Promo Code"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Input
              placeholder="Promo code"
              className="h-5.4 focus:bg-white focus:border-primaryBlue hover:border-primaryBlue"
            />
          </Form.Item>
          <Form.Item
            name="discountAmount"
            label="Discount Amount ($)"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <InputNumber
              placeholder="Discount amount ($)"
              className="h-5.4 focus:bg-white w-full focus:border-primaryBlue hover:border-primaryBlue !pt-1"
            />
          </Form.Item>
          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[{ required: true, message: "This field is required" }]}
          >
            <DatePicker
              placeholder="Start Date"
              format="YYYY-MM-DD HH:mm:ss"
              disabledDate={disabledStartDate}
              onChange={(date) => setStartDate(date)}
              showTime={{
                defaultValue: dayjs("00:00:00", "HH:mm:ss"),
              }}
              className="h-5.4 focus:bg-white w-full focus:border-primaryBlue hover:border-primaryBlue"
            />
          </Form.Item>
          <Form.Item
            name="expiryDate"
            label="Expiry Date"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <DatePicker
              placeholder="Expiry date"
              format="YYYY-MM-DD HH:mm:ss"
              disabledDate={disabledEndDate}
              showTime={{
                defaultValue: dayjs("00:00:00", "HH:mm:ss"),
              }}
              className="h-5.4 focus:bg-white w-full focus:border-primaryBlue hover:border-primaryBlue"
            />
          </Form.Item>

          <div className="col-span-2 md:mb-0 mb-2 sm:mb-0">
            <Form.Item name="clickAction" label="Promo Code Type">
              <Radio.Group
                onChange={(key) => setSelectedKey(key.target.value)}
                value={selectedKey}
                checked={selectedKey}
                defaultValue={selectedKey}
                key={selectedKey}
                checkedChildren={selectedKey}
              >
                <Radio value={"user"}>User</Radio>
                <Radio value={"service"}>Service</Radio>
                <Radio value={"category"}>Category</Radio>
                <Radio value={"global"}>Global</Radio>
                <Radio value={"variant"}>Variant</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          {selectedKey === "user" && (
            <Form.Item
              name="userId"
              label="User(s)"
              rules={[
                {
                  required: true,
                  message: "This field is required",
                },
              ]}
            >
              <Dropdown
                className={"!rounded-lg"}
                placeholder="User"
                isUser={true}
                loadMoreData={(e) => {
                  loadMoreUserData(e);
                }}
                data={users}
                loader={isUserLoading}
                mode={"multiple"}
                defaultValue={form.getFieldValue("userId")}
                fetchData={getCustomers}
              />
            </Form.Item>
          )}

          {selectedKey === "service" && (
            <>
              <Form.Item
                name="wenderId"
                label="Vendor"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <Dropdown
                  className={"h-5 !rounded-lg"}
                  placeholder="Vendor"
                  isBusiness={true}
                  loadMoreData={(e) => {
                    loadMoreData(e);
                  }}
                  data={businessNames}
                  onChange={handleBusinessChange}
                  loader={isBusinessLoading}
                  defaultValue={form.getFieldValue("wenderId")}
                  fetchData={(payload) => dispatch(fetchBusiness(payload))}
                />
              </Form.Item>
              <Form.Item
                name="serviceId"
                label="Service"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <Dropdown
                  className={"h-5 !rounded-lg"}
                  placeholder="Service"
                  isService={true}
                  loadMoreData={(e) => {
                    loadMoreServiceData(e);
                  }}
                  data={services}
                  loader={isServiceLoading}
                  defaultValue={form.getFieldValue("serviceId")}
                />
              </Form.Item>
            </>
          )}
          {selectedKey === "variant" && (
            <>
              <Form.Item
                name="wenderId"
                label="Vendor"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <Dropdown
                  className={"h-5 !rounded-lg"}
                  placeholder="Vendor"
                  isBusiness={true}
                  loadMoreData={(e) => {
                    loadMoreData(e);
                  }}
                  data={businessNames}
                  onChange={handleBusinessChange}
                  loader={isBusinessLoading}
                  defaultValue={form.getFieldValue("wenderId")}
                  fetchData={(payload) => dispatch(fetchBusiness(payload))}
                />
              </Form.Item>

              <Form.Item
                name="variantId"
                label="Variant"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <SubMenuDropdown
                  className={"h-5 !rounded-lg"}
                  placeholder="Variant"
                  isService={true}
                  loadMoreData={(e) => {
                    loadMoreServiceData(e);
                  }}
                  data={services}
                  loader={isServiceLoading}
                  defaultValue={form.getFieldValue("variantId")}
                />
              </Form.Item>
            </>
          )}
          {selectedKey === "category" && (
            <Form.Item
              name="categoryId"
              label="Category"
              rules={[
                {
                  required: true,
                  message: "This field is required",
                },
              ]}
            >
              <Dropdown
                className={"h-5 !rounded-lg"}
                placeholder="Category"
                loadMoreData={(e) => false}
                data={categories}
                loader={isCategoryLoading}
                defaultValue={form.getFieldValue("categoryId")}
              />
            </Form.Item>
          )}

          {selectedKey === "global" && (
            <Form.Item
              name="global"
              label="For All Users"
              rules={[
                {
                  required: !allUsersAllServices,
                  message: "This field is required",
                },
              ]}
            >
              <Checkbox
                checked={allUsersAllServices}
                onChange={(e) => setAllUsersAllServices(e.target.checked)}
              >
                Apply to all users for all services
              </Checkbox>
            </Form.Item>
          )}

          <div className="mt-2">
            <Button
              className="w-full"
              color={colors.primaryPurple}
              title={`${editPromoCode ? "Update" : "Create"} Promo Code`}
              htmlType="submit"
              loading={loader}
            />
          </div>
        </div>
      </Form>
    </div>
  );
};

export default PromoCodeForm;
